import { useState, useEffect, useCallback, useRef } from 'react'
import { authenticatedFetchJson } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'
import logger from '../utils/logger'

const useBillingInvoiceData = ({ campaignId, billingCustomerId, status, invoiceType, page = 1, limit = 25, sortBy = 'created_at', sortDir = 'desc' } = {}) => {
  const [invoices, setInvoices] = useState([])
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 25, total_pages: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const abortControllerRef = useRef(null)

  const fetchInvoices = useCallback(async (signal) => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      if (campaignId) params.set('campaign_id', campaignId)
      if (billingCustomerId) params.set('billing_customer_id', billingCustomerId)
      if (status) params.set('status', status)
      if (invoiceType) params.set('invoice_type', invoiceType)
      params.set('page', String(page))
      params.set('limit', String(limit))
      params.set('sort_by', sortBy)
      params.set('sort_dir', sortDir)

      const url = `${apiEndpoints.billingInvoices}?${params.toString()}`
      const response = await authenticatedFetchJson(url, { signal })
      setInvoices(response?.data || [])
      setMeta(response?.meta || { total: 0, page: 1, limit: 25, total_pages: 0 })
    } catch (err) {
      if (err.name === 'AbortError') return
      logger.error('Failed to fetch invoices:', err)
      setError('Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }, [campaignId, billingCustomerId, status, invoiceType, page, limit, sortBy, sortDir])

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller
    fetchInvoices(controller.signal)
    return () => controller.abort()
  }, [fetchInvoices])

  const refetch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller
    return fetchInvoices(controller.signal)
  }, [fetchInvoices])

  return { invoices, meta, loading, error, refetch }
}

export default useBillingInvoiceData
